/**
 * Transport for the caller's own UI preferences (`GET/PUT /me/settings`, API schema 110).
 *
 * These replace what used to be per-page localStorage: a preference now follows the user to
 * another browser or workstation, and the server is the single source of truth. Nothing here is
 * user-identifying — the endpoint always scopes to the authenticated caller, so there is no user
 * id to pass and no way to address anyone else's preferences.
 */

import { apiClient } from '@/shared/api/client';

/**
 * A list screen's remembered state. Both fields are optional and written independently: the
 * page-size selector saves `pageSize`, the customize-columns panel saves `columns`. Mirrors
 * `listSettingValueSchema` in services/api/src/user-settings/user-settings.catalog.ts — the API
 * rejects anything else.
 */
export interface UserSettingValue {
  pageSize?: number;
  columns?: string[];
}

/** The caller's whole preference set, keyed by setting key (e.g. `list.vehicles`). */
export type UserSettingsMap = Record<string, UserSettingValue>;

/** Every preference of the authenticated caller, in one request. */
export async function fetchUserSettings(): Promise<UserSettingsMap> {
  const { data } = await apiClient.get<{ data: UserSettingsMap }>('/me/settings');
  return data.data;
}

/**
 * Write one preference. The PUT REPLACES the key's whole value, so the caller must send the
 * merged object — the provider does that from the cached set (see UserSettingsProvider), which
 * is what keeps a page-size change from wiping a saved column layout.
 */
export async function putUserSetting(key: string, value: UserSettingValue): Promise<void> {
  await apiClient.put(`/me/settings/${encodeURIComponent(key)}`, { value });
}
